// External Intake & Collection v1.0 PR 4 — portal HTTP client.
//
// Mirrors src/api/client.ts but is hard-coded to the /api/portal/* prefix:
// the portal must never reach a staff endpoint. Same fetch contract
// (ApiResponse envelope, credentials: "include" so the Portal cookie is
// sent, JSON body), same ApiError shape so consumers can branch on
// status codes (401 → re-redeem, 403 → out-of-scope, 4xx → toast).

const API_BASE = "/api/portal";

export class PortalApiError extends Error {
  status: number;
  errors: string[];
  constructor(message: string, status: number, errors: string[]) {
    super(message);
    this.status = status;
    this.errors = errors;
  }
}

interface ApiResponse<T> {
  data?: T;
  message?: string;
  errors?: string[];
}

// Global 401 handler. The portal session cookie can be invalidated out from
// under an open tab (e.g. the user signs out in another tab). Rather than
// surfacing a raw "HTTP 401" in whatever view happened to fire a request,
// the PortalAuthProvider registers a handler here that redirects the tab to
// the sign-in page. Registered once; null when no provider is mounted.
let onUnauthorized: (() => void) | null = null;
export function setPortalUnauthorizedHandler(handler: (() => void) | null) {
  onUnauthorized = handler;
}

type Query = Record<string, string | number | boolean | null | undefined>;
type RequestOptions = Omit<RequestInit, "body" | "headers"> & {
  body?: unknown;
  headers?: Record<string, string>;
  query?: Query;
};

function buildUrl(path: string, query?: Query): string {
  const base = path.startsWith("/") ? `${API_BASE}${path}` : `${API_BASE}/${path}`;
  if (!query) return base;
  const params = new URLSearchParams();
  for (const [k, v] of Object.entries(query)) {
    if (v === undefined || v === null || v === "") continue;
    params.append(k, String(v));
  }
  const qs = params.toString();
  return qs ? `${base}?${qs}` : base;
}

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { body, headers, query, ...rest } = options;
  const isFormData = body instanceof FormData;

  const response = await fetch(buildUrl(path, query), {
    ...rest,
    credentials: "include",
    headers: {
      Accept: "application/json",
      ...(isFormData ? {} : body !== undefined ? { "Content-Type": "application/json" } : {}),
      ...headers,
    },
    body: isFormData ? (body as FormData) : body !== undefined ? JSON.stringify(body) : undefined,
  });

  const contentType = response.headers.get("content-type") ?? "";
  const payload = contentType.includes("application/json")
    ? ((await response.json()) as ApiResponse<T>)
    : null;

  if (!response.ok) {
    // Notify the auth provider so an authenticated tab whose session was
    // revoked elsewhere lands on sign-in instead of showing the 401. The
    // handler itself decides whether to act (it no-ops when there was no
    // known session, e.g. the /auth/me probe on a public page).
    if (response.status === 401) onUnauthorized?.();
    const fallback = `Request failed (HTTP ${response.status})`;
    const errors = (payload?.errors ?? [])
      .map((e) => (typeof e === "string" ? e.trim() : ""))
      .filter(Boolean);
    const message = payload?.message?.trim()
      || errors[0]
      || response.statusText.trim()
      || fallback;
    throw new PortalApiError(message, response.status, errors.length ? errors : [message]);
  }

  if (payload && "data" in payload) return payload.data as T;
  return undefined as T;
}

/** Request returning the raw response body as a Blob (e.g. a generated PDF
 *  preview). Same scope/cookie contract as {@link request}; surfaces the JSON
 *  envelope message on a non-2xx so callers get a useful error. */
async function requestBlob(
  path: string,
  options: { method?: string; query?: Query; body?: unknown } = {},
): Promise<Blob> {
  const { method = "GET", query, body } = options;
  const response = await fetch(buildUrl(path, query), {
    method,
    credentials: "include",
    ...(body !== undefined
      ? { headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) }
      : {}),
  });
  if (!response.ok) {
    if (response.status === 401) onUnauthorized?.();
    let message = `Request failed (HTTP ${response.status})`;
    try {
      const payload = (await response.json()) as ApiResponse<unknown>;
      const errors = (payload?.errors ?? [])
        .map((e) => (typeof e === "string" ? e.trim() : ""))
        .filter(Boolean);
      message = payload?.message?.trim() || errors[0] || message;
    } catch {
      /* non-JSON error body — keep the HTTP fallback */
    }
    throw new PortalApiError(message, response.status, [message]);
  }
  return response.blob();
}

/** A downloaded blob together with the name the SERVER asked it be saved under. */
export interface NamedBlob {
  blob: Blob;
  /** From `Content-Disposition`, or null when the response named none. */
  fileName: string | null;
}

/**
 * `postBlob`, plus the server's `Content-Disposition` file name.
 *
 * A separate entry rather than a widening of `requestBlob` because eight other
 * callers share that one and none of them needs this.
 *
 * It exists because a blob URL carries no headers: `URL.createObjectURL` +
 * `anchor.download` is the only way a fetched PDF reaches the disk, and that
 * path drops `Content-Disposition` entirely. Without this the caller has to
 * re-derive the name it was already told, which is a second place for the
 * naming rule to live and drift from.
 */
async function postNamedBlob(path: string, body?: unknown): Promise<NamedBlob> {
  const response = await fetch(buildUrl(path), {
    method: "POST",
    credentials: "include",
    ...(body !== undefined
      ? { headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) }
      : {}),
  });
  if (!response.ok) {
    if (response.status === 401) onUnauthorized?.();
    let message = `Request failed (HTTP ${response.status})`;
    try {
      const payload = (await response.json()) as ApiResponse<unknown>;
      const errors = (payload?.errors ?? [])
        .map((e) => (typeof e === "string" ? e.trim() : ""))
        .filter(Boolean);
      message = payload?.message?.trim() || errors[0] || message;
    } catch {
      /* non-JSON error body — keep the HTTP fallback */
    }
    throw new PortalApiError(message, response.status, [message]);
  }
  // `filename="…"` (quoted or bare). RFC 5987's `filename*` is not parsed:
  // ASP.NET only emits it for non-ASCII names and nothing here produces one.
  const disposition = response.headers.get("Content-Disposition") ?? "";
  const match = /filename\s*=\s*"?([^";]+)"?/i.exec(disposition);
  return { blob: await response.blob(), fileName: match?.[1]?.trim() || null };
}

export const portalApi = {
  get: <T>(path: string, query?: Query) => request<T>(path, { method: "GET", query }),
  post: <T>(path: string, body?: unknown) => request<T>(path, { method: "POST", body }),
  put: <T>(path: string, body?: unknown) => request<T>(path, { method: "PUT", body }),
  patch: <T>(path: string, body?: unknown) => request<T>(path, { method: "PATCH", body }),
  // Named to match the staff client's `delete` (src/api/client.ts) rather than
  // inventing a second verb for the same method. `delete` is a reserved word but
  // a perfectly legal property name, and the staff client has used it for years.
  delete: <T>(path: string, body?: unknown) => request<T>(path, { method: "DELETE", body }),
  upload: <T>(path: string, form: FormData) => request<T>(path, { method: "POST", body: form }),
  getBlob: (path: string, query?: Query) => requestBlob(path, { query }),
  postBlob: (path: string, body?: unknown) => requestBlob(path, { method: "POST", body }),
  postNamedBlob,
};
